// Lib
import React, { ReactElement, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { clearElementFilter } from '../../element/elementFilter/elementFilterActions';

// Selectors
import { getIsCurrentVisibleBoardFetched } from '../../element/board/boardSelector';
import { getElementFilterQuerySelector } from '../../element/elementFilter/elementFilterSelector';

// Components
import Toast from '../../structural/toast/Toast';
import ToastDismissButton from '../../structural/toast/ToastDismissButton';

// Styles
import './ClearSearchToast.scss';

/**
 * Calculate the toast content within a subcomponent of the <Toast> component
 * to ensure that the element filter query is up-to-date when the toast is shown.
 */
const ClearSearchToastContent: React.FC = () => {
    const elementFilterQuery = useSelector(getElementFilterQuerySelector);
    const [searchedQuery, setSearchedQuery] = React.useState(elementFilterQuery);

    // Only update the query if the filter query changes & exists
    //  If the filter query is cleared, we want to show the previous query while it's animating out
    useEffect(() => {
        if (elementFilterQuery) setSearchedQuery(elementFilterQuery);
    }, [elementFilterQuery]);

    return (
        <>
            <span className="verb">Showing results for&nbsp;</span>
            <span className="query">‘{searchedQuery}’</span>
            <ToastDismissButton />
        </>
    );
};

const ClearSearchToast = (): ReactElement | null => {
    const currentBoardFetched = useSelector(getIsCurrentVisibleBoardFetched);
    const elementFilterQuery = useSelector(getElementFilterQuerySelector);

    const dispatch = useDispatch();
    const dispatchClearElementFilter = useCallback(() => dispatch(clearElementFilter()), []);

    if (!elementFilterQuery) return null;
    if (!currentBoardFetched) return null;

    return (
        <Toast className="ClearSearchToast" onClick={dispatchClearElementFilter}>
            <ClearSearchToastContent />
        </Toast>
    );
};

export default ClearSearchToast;
